import { colors } from '../../styles/colors';
import { MOIS } from '../../utils/constants';

/**
 * TempsFormFields - Champs du formulaire de saisie des temps
 *
 * Extrait de TempsForm.js pour une meilleure lisibilité.
 */
export default function TempsFormFields({
  formData,
  consultants,
  prestataires,
  clientsFiltres,
  projetsFiltres,
  annees,
  factureBloquee,
  handleChange,
  isSuperAdmin,
  user
}) {
  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: `2px solid ${colors.secondary.lightTeal}`,
    borderRadius: '0.5rem',
    fontSize: '0.9375rem',
    fontFamily: '"Noto Sans", sans-serif',
    color: colors.primary.gray,
    backgroundColor: colors.text.white,
    transition: 'all 200ms',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: colors.primary.gray,
    marginBottom: '0.5rem',
    fontFamily: '"Noto Sans", sans-serif'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem'
  };

  const handleFocus = (e) => e.target.style.borderColor = colors.primary.teal;
  const handleBlur = (e) => e.target.style.borderColor = colors.secondary.lightTeal;

  const getDisabledStyle = (isDisabled) => ({
    ...inputStyle,
    backgroundColor: isDisabled ? colors.secondary.lightGray : colors.text.white,
    color: isDisabled ? colors.secondary.mediumGray : colors.primary.gray,
    cursor: isDisabled ? 'not-allowed' : 'pointer'
  });

  return (
    <>
      {/* Ligne 1 : Année, Mois, Consultant */}
      <div style={gridStyle}>
        <div>
          <label style={labelStyle}>
            Année <span style={{ color: colors.primary.teal }}>*</span>
          </label>
          <select
            name="Année"
            value={formData['Année']}
            onChange={handleChange}
            required
            style={inputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            {annees.map(annee => (
              <option key={annee} value={annee}>{annee}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>
            Mois <span style={{ color: colors.primary.teal }}>*</span>
          </label>
          <select
            name="Mois"
            value={formData['Mois']}
            onChange={handleChange}
            required
            style={inputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            {MOIS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>
            Consultant <span style={{ color: colors.primary.teal }}>*</span>
          </label>
          <select
            name="Consultant"
            value={formData['Consultant'][0] || ''}
            onChange={handleChange}
            required
            disabled={!isSuperAdmin()}
            style={getDisabledStyle(!isSuperAdmin())}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            <option value="">-- Sélectionner --</option>
            {consultants
              .filter(c => isSuperAdmin() || c.id === user?.consultant_id)
              .map(c => (
                <option key={c.id} value={c.id}>{c.fields.Nom}</option>
              ))}
          </select>
        </div>
      </div>

      {/* Ligne 2 : Prestataire, Client, Projet */}
      <div style={gridStyle}>
        <div>
          <label style={labelStyle}>
            Prestataire <span style={{ color: colors.primary.teal }}>*</span>
          </label>
          <select
            name="Prestataire"
            value={formData['Prestataire'][0] || ''}
            onChange={handleChange}
            required
            style={inputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            <option value="">-- Sélectionner --</option>
            {prestataires.map(p => (
              <option key={p.id} value={p.id}>{p.nom}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{
            ...labelStyle,
            color: !formData['Prestataire'][0] ? colors.secondary.mediumGray : colors.primary.gray
          }}>
            Client <span style={{ color: !formData['Prestataire'][0] ? colors.secondary.mediumGray : colors.primary.teal }}>*</span>
          </label>
          <select
            name="Client"
            value={formData['Client'][0] || ''}
            onChange={handleChange}
            required
            disabled={!formData['Prestataire'][0]}
            style={getDisabledStyle(!formData['Prestataire'][0])}
            onFocus={(e) => { if (formData['Prestataire'][0]) handleFocus(e); }}
            onBlur={handleBlur}
          >
            <option value="">-- Sélectionner --</option>
            {clientsFiltres.map(client => (
              <option key={client.id} value={client.id}>{client.nom}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{
            ...labelStyle,
            color: !formData['Client'][0] ? colors.secondary.mediumGray : colors.primary.gray
          }}>
            Projet <span style={{ color: !formData['Client'][0] ? colors.secondary.mediumGray : colors.primary.teal }}>*</span>
          </label>
          <select
            name="Projet"
            value={formData['Projet'][0] || ''}
            onChange={handleChange}
            required
            disabled={!formData['Client'][0]}
            style={getDisabledStyle(!formData['Client'][0])}
            onFocus={(e) => { if (formData['Client'][0]) handleFocus(e); }}
            onBlur={handleBlur}
          >
            <option value="">-- Sélectionner --</option>
            {projetsFiltres.map(p => (
              <option key={p.id} value={p.id}>
                {p.fields['Nom du projet'] || p.fields.Nom}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Ligne 3 : Nombre de jours, Taux journalier */}
      <div style={gridStyle}>
        <div>
          <label style={labelStyle}>
            Nombre de jours <span style={{ color: colors.primary.teal }}>*</span>
          </label>
          <input
            type="number"
            name="Nombre de jours"
            value={formData['Nombre de jours']}
            onChange={handleChange}
            step="0.5"
            min="0"
            required
            disabled={!!factureBloquee}
            style={getDisabledStyle(!!factureBloquee)}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        <div>
          <label style={labelStyle}>
            Taux journalier (€) <span style={{ color: colors.primary.teal }}>*</span>
          </label>
          <input
            type="number"
            name="Taux journalier (€)"
            value={formData['Taux journalier (€)']}
            onChange={handleChange}
            step="0.01"
            min="0"
            required
            disabled={!!factureBloquee}
            style={getDisabledStyle(!!factureBloquee)}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        <div />
      </div>

      {/* Ligne 4 : Notes */}
      <div>
        <label style={labelStyle}>Notes</label>
        <textarea
          name="Notes"
          value={formData['Notes']}
          onChange={handleChange}
          rows="3"
          disabled={!!factureBloquee}
          style={{
            ...inputStyle,
            resize: 'vertical',
            backgroundColor: factureBloquee ? colors.secondary.lightGray : colors.text.white,
            cursor: factureBloquee ? 'not-allowed' : 'text'
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Notes optionnelles..."
        />
      </div>

      {/* Checkbox : Exclure du calcul TMP */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.5rem 0'
      }}>
        <input
          type="checkbox"
          id="tmpexcl"
          name="tmpexcl"
          checked={formData['tmpexcl']}
          onChange={handleChange}
          disabled={!!factureBloquee}
          style={{
            width: '18px',
            height: '18px',
            cursor: factureBloquee ? 'not-allowed' : 'pointer',
            accentColor: colors.primary.teal
          }}
        />
        <label
          htmlFor="tmpexcl"
          style={{
            ...labelStyle,
            marginBottom: 0,
            cursor: factureBloquee ? 'not-allowed' : 'pointer',
            color: factureBloquee ? colors.secondary.mediumGray : colors.primary.gray
          }}
        >
          Exclure du calcul TMP
        </label>
      </div>
    </>
  );
}
